import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import useCurrentOrganization from '@/hooks/useCurrentOrganization';
import { Loader2 } from 'lucide-react';
import AccessDeniedInline from './AccessDeniedInline';

// Mapeo de roles de organization_members a USER_ROLES del frontend
const ORG_ROLE_TO_FRONTEND = {
  dentist: 'therapist',
  clinic_admin: 'clinic',
  assistant: 'assistant',
};

// B11a: roles primarios que requieren onboarding de clínica completo
// (existir como member de al menos una org). Los demás (patient, admin,
// superadmin, lab) no se bloquean.
const ROLES_REQUIRING_CLINIC_ONBOARDING = ['therapist', 'clinic'];

// Rutas donde NO aplicamos el gate (el wizard mismo, settings, etc.)
const ONBOARDING_EXEMPT_PATHS = [
  '/dashboard/profile',
  '/dashboard/membership',
  '/dashboard/membresia',
];

// Approval gate (pre-launch): roles que requieren aprobación admin antes
// de acceder al dashboard. patient y assistant NO requieren — el paciente
// se autoregistra para su propia ficha, el assistant viene por invitación.
const ROLES_REQUIRING_APPROVAL = ['therapist', 'clinic'];

const RoleGuard = ({ children, allowedRoles = [] }) => {
  const { user, profile, loading } = useAuth();
  const { effectiveRole, userOrgRoles = [], loading: orgLoading } = useCurrentOrganization();
  const location = useLocation();

  // Esperar a que tanto auth como org context terminen de cargar
  if (loading || orgLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Approval gate (pre-launch): si el user es pro y NO está aprobado por
  // un admin, redirigir a /auth/pending-approval. Excepción: si ya está
  // en esa página, dejarlo (evita loop).
  const approvalStatus = profile?.approval_status;
  const profileRoleRaw = profile?.role || user.user_metadata?.role;
  const requiresApproval =
    profileRoleRaw &&
    ROLES_REQUIRING_APPROVAL.includes(profileRoleRaw) &&
    approvalStatus &&
    approvalStatus !== 'approved';
  if (requiresApproval && location.pathname !== '/auth/pending-approval') {
    return <Navigate to="/auth/pending-approval" replace />;
  }

  // Permitir acceso si CUALQUIERA de los roles del usuario matchea:
  //  - profile.role (rol primario)
  //  - effectiveRole (priority dentist > clinic_admin > assistant)
  //  - cualquier rol activo en organization_members (mapeado a frontend)
  const profileRole = profile?.role || user.user_metadata?.role;
  const orgEffectiveRole = effectiveRole;
  const allOrgRolesMapped = userOrgRoles.map(r => ORG_ROLE_TO_FRONTEND[r] || r);

  // B11a gate: usuarios pro sin organization_members deben completar wizard
  // "Mi Clínica" antes de poder usar el resto de la app. Sin esto, las RLS
  // de patients/appointments/etc. bloquean todo y la app queda "rota".
  const needsClinicOnboarding =
    profileRole &&
    ROLES_REQUIRING_CLINIC_ONBOARDING.includes(profileRole) &&
    userOrgRoles.length === 0;

  const isExemptPath = ONBOARDING_EXEMPT_PATHS.some((p) => location.pathname.startsWith(p));

  if (needsClinicOnboarding && !isExemptPath) {
    // Therapist va a "Mis Lugares de Atención" (crea clinic propia → trigger crea org_members).
    // Clinic admin va a "Datos de la Clínica" (mismo destino lógico, otro tab).
    const wizardTab = profileRole === 'clinic' ? 'clinic-info' : 'my-clinics';
    return <Navigate to={`/dashboard/profile?tab=${wizardTab}&onboarding=required`} replace />;
  }

  if (allowedRoles.length === 0) return children;

  const hasAccess =
    (profileRole && allowedRoles.includes(profileRole)) ||
    (orgEffectiveRole && allowedRoles.includes(orgEffectiveRole)) ||
    allOrgRolesMapped.some(r => allowedRoles.includes(r));

  if (!hasAccess) {
    // User está logueado pero su rol no matchea. Antes hacíamos
    // Navigate('/dashboard') lo que: (a) podía causar loops si la ruta de
    // origen ya era /dashboard, (b) podía rebotar al RoleLandingRedirect
    // y mandar al user a un dashboard que tampoco le corresponde,
    // (c) algunas combinaciones terminaban "rebotando" al /auth/login
    // dando la sensación de que la sesión se rompió.
    //
    // Comportamiento nuevo: mostrar AccessDeniedInline (mismo componente
    // que usa PermissionGuard en admin). El user mantiene sesión activa,
    // ve mensaje claro, y decide adónde ir (CTA "Volver al inicio"
    // apunta a /dashboard que ejecuta RoleLandingRedirect correctamente).
    return <AccessDeniedInline />;
  }

  return children;
};

export default RoleGuard;